import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, "")}${path}`
  }
  return path
}

const resolveApiOrigin = (): string => {
  if (!API_BASE_URL) return ""

  try {
    if (/^https?:\/\//i.test(API_BASE_URL)) {
      return new URL(API_BASE_URL).origin
    }

    if (typeof window !== "undefined") {
      return new URL(API_BASE_URL, window.location.origin).origin
    }
  } catch {
    return ""
  }

  return ""
}

export interface SystemConfigDto {
  systemName: string
  companyName: string
  logoUrl?: string | null
  faviconUrl?: string | null
  systemUrl?: string | null
  supportEmail?: string | null
  supportPhone?: string | null
  env?: string | null
}

export interface SystemConfigUpsertRequest {
  systemName: string
  companyName?: string | null
  logoUrl?: string | null
  faviconUrl?: string | null
  systemUrl?: string | null
  supportEmail?: string | null
  supportPhone?: string | null
}

export interface SystemAssetUploadResponseDto {
  fileName: string
  storedFileName: string
  assetUrl: string
  contentType: string
  size: number
}

export function normalizeSystemAssetUrl(value?: string | null): string {
  if (!value) return ""
  const trimmed = value.trim()
  if (!trimmed) return ""
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:")) {
    return trimmed
  }

  const normalizedPath = trimmed.startsWith("/") ? trimmed : `/${trimmed}`
  const apiOrigin = resolveApiOrigin()

  if (!apiOrigin) {
    return normalizedPath
  }

  return `${apiOrigin}${normalizedPath}`
}

export async function getSystemConfig(): Promise<SystemConfigDto> {
  const response = await axios.get<SystemConfigDto>(buildUrl("/api/v1/system-configs"))
  return response.data
}

export async function upsertSystemConfig(payload: SystemConfigUpsertRequest): Promise<SystemConfigDto> {
  const response = await axios.post<SystemConfigDto>(buildUrl("/api/v1/system-configs"), payload)
  return response.data
}

export async function uploadSystemLogo(file: File): Promise<SystemAssetUploadResponseDto> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await axios.post<SystemAssetUploadResponseDto>(
    buildUrl("/api/v1/system-configs/logo/upload"),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  )

  return {
    ...response.data,
    assetUrl: normalizeSystemAssetUrl(response.data.assetUrl),
  }
}
